import { DndContext } from '@dnd-kit/core';
import { Box, Stack } from '@rossum/ui/material';
import { useDragPosition } from './useDragPosition';

const DEFAULT_SIZE = 300;
const MAX_SIZE = 600;

type PaneConfig = {
  min: number;
  max: number;
  initial: number;
};

type Direction = 'horizontal' | 'vertical';

type PaneProps = {
  storageKey?: string;
  children: React.ReactNode;
  paneConfig?: PaneConfig;
  direction?: Direction;
};

const defaultPaneConfig: PaneConfig = {
  min: DEFAULT_SIZE,
  max: MAX_SIZE,
  initial: DEFAULT_SIZE,
};

const Pane = ({
  children,
  storageKey,
  paneConfig,
  direction = 'vertical',
}: PaneProps) => {
  const resolvedConfig = paneConfig ?? defaultPaneConfig;
  const isVerticalHandle = direction === 'vertical';

  const {
    position,
    setNodeRef,
    attributes,
    listeners,
    isDragging,
    containerRef,
  } = useDragPosition({
    storageKey,
    direction,
    ...resolvedConfig,
  });

  return (
    <Stack
      ref={containerRef}
      style={
        isVerticalHandle
          ? { width: position, flex: '0 0 auto' }
          : { height: position, flex: '0 0 auto' }
      }
      width={isVerticalHandle ? undefined : 1}
      height={isVerticalHandle ? 1 : undefined}
      position="relative"
      direction={isVerticalHandle ? 'row' : 'column'}
    >
      {children}
      <Box
        sx={{
          '&:hover div': { opacity: 1 },
          position: 'absolute',
          ...(isVerticalHandle
            ? { right: -10, top: 0, bottom: 0 }
            : { bottom: -10, left: 0, right: 0 }),
          zIndex: 1000,
        }}
        width={isVerticalHandle ? undefined : 1}
        height={isVerticalHandle ? 1 : undefined}
        ref={setNodeRef}
        {...listeners}
        {...attributes}
      >
        <Box
          width={isVerticalHandle ? 10 : 1}
          height={isVerticalHandle ? 1 : 10}
          sx={{
            ...(isVerticalHandle
              ? {
                  borderLeft: t => `3px solid ${t.palette.background.default}`,
                  borderRight: t => `3px solid ${t.palette.background.default}`,
                }
              : {
                  borderTop: t => `3px solid ${t.palette.background.default}`,
                  borderBottom: t =>
                    `3px solid ${t.palette.background.default}`,
                }),
            opacity: isDragging ? 1 : 0,
            backgroundColor: t =>
              isDragging ? t.palette.primary.main : t.palette.action.hover,
            cursor: isVerticalHandle ? 'col-resize' : 'row-resize',
          }}
        />
      </Box>
    </Stack>
  );
};

type ResizablePaneProps = {
  storageKey?: string;
  children: React.ReactNode;
  paneConfig?: PaneConfig;
  direction?: Direction;
};

export const ResizablePane = ({
  children,
  storageKey,
  paneConfig,
  direction = 'vertical',
}: ResizablePaneProps) => (
  <DndContext
    onDragEnd={({ active }) => {
      if (storageKey && active.data.current?.['position'])
        localStorage.setItem(
          storageKey,
          String(active.data.current['position'])
        );
    }}
  >
    <Pane storageKey={storageKey} paneConfig={paneConfig} direction={direction}>
      {children}
    </Pane>
  </DndContext>
);
